// apps/web/app/microgrid/lit/components/Nav.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import AlwaysLitLogo from './AlwaysLitLogo';
import { track, routePing, setParentHash } from '../components/ga';
import './styles/Nav.css';

type MenuNode = {
  id: number;
  title: string;
  href: string;
  target?: string;
  classes?: string[];
  children?: MenuNode[];
};

const WP_MENU_ENDPOINT = 'https://alwayslittx.com/wp-json/al/v1/menu?location=primary&tree=1&depth=3';

const Nav: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<MenuNode[]>([]);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [hoverId, setHoverId] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MENU_ENDPOINT, { headers: { Accept: 'application/json' }, signal: ctrl.signal, cache: 'no-store' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data: MenuNode[] = await r.json();
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; ctrl.abort(); };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-open', open);
  }, [open]);

  const isAnchor = (href?: string) => !!href && href.startsWith('#');

  const scrollToHash = (href?: string) => {
    if (!href || !href.startsWith('#')) return false;
    const id = href.slice(1);
    const el = document.getElementById(id);
    if (!el) return false;
    const varH = getComputedStyle(document.documentElement).getPropertyValue('--nav-h').trim();
    const offset = parseInt(varH || '0', 10) || 100;
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    return true;
  };

  const signalSkipAnim = () => window.dispatchEvent(new Event('al:skip-anim'));

  const handleHashNav = () => {
    if (location.hash) signalSkipAnim();
    requestAnimationFrame(() => {
      scrollToHash(location.hash);
      // record route/hash in parent
      routePing();
      track('hash_navigate', { hash: location.hash });
    });
  };

  useEffect(() => {
    if (location.hash) handleHashNav();
    const onHash = () => { setOpen(false); handleHashNav(); };
    const onResize = () => { if (window.innerWidth >= 769) setOpen(false); };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const onLinkClick = (href?: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isAnchor(href)) {
      e.preventDefault();
      signalSkipAnim();
      setParentHash(href!);     // update parent hash (and triggers routePing there)
      handleHashNav();
      track('menu_click', { link_type: 'anchor', href });
    } else {
      track('menu_click', { link_type: 'url', href });
    }
    setOpen(false);
  };

  const isCTA = (n: MenuNode) => {
    const href = n.href || '';
    let path = href;
    try { path = new URL(href, location.origin).pathname; } catch {}
    path = path.replace(/\/+$/, '');
    return (n.classes?.some((c) => /^(cta|button)$/i.test(c)) ?? false) || /\/proposal\/new$/i.test(path) || /\/vision-engine$/i.test(path);
  };

  const toggleMobileSub = (id: number) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      track('submenu_toggle', { id, open: !next.has(id) });
      return next;
    });
  };
  const openNow = (id: number) => { if (timer.current) clearTimeout(timer.current); setHoverId(id); };
  const closeSoon = () => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => setHoverId(null), 420); };

  const renderTop = (nodes: MenuNode[]) => (
    <>
      {nodes.map((n) => {
        const hasChildren = (n.children?.length ?? 0) > 0;
        const isOpenDesktop = hoverId === n.id;
        const isOpenMobile = openIds.has(n.id);
        return (
          <div
            key={n.id}
            className={`has-sub${isOpenDesktop || isOpenMobile ? ' open' : ''}`}
            onMouseEnter={() => openNow(n.id)}
            onMouseLeave={closeSoon}
          >
            <a
              href={n.href}
              onClick={onLinkClick(n.href)}
              className={isCTA(n) ? 'cta-button' : undefined}
              target={isCTA(n) ? '_top' : n.target}
              rel={n.target === '_blank' ? 'noopener' : undefined}
            >
              <span dangerouslySetInnerHTML={{ __html: n.title }} />
              {hasChildren && <span className="caret" aria-hidden="true">▾</span>}
            </a>

            {hasChildren && (
              <>
                <button
                  type="button"
                  aria-label="Toggle submenu"
                  className="subtoggle"
                  onClick={() => toggleMobileSub(n.id)}
                  style={{ display: 'none' }}
                />
                <div className="nav-dropdown">
                  {n.children!.map((c) => (
                    <a
                      key={c.id}
                      href={c.href}
                      onClick={onLinkClick(c.href)}
                      className={isCTA(c) ? 'cta-button' : undefined}
                      target={isCTA(c) ? '_top' : c.target}
                      rel={c.target === '_blank' ? 'noopener' : undefined}
                      dangerouslySetInnerHTML={{ __html: c.title }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <nav>
      <div className="logo lit" aria-hidden="false">
        <AlwaysLitLogo />
      </div>

      <button
        className="nav-toggle"
        aria-label="Open menu"
        aria-controls="primary-nav"
        aria-expanded={open}
        onClick={() => { setOpen((v) => !v); track('menu_toggle', { open: !open }); }}
      >
        <span /><span /><span />
      </button>

      <div id="primary-nav" className={`nav-links${open ? ' is-open' : ''}`} aria-hidden={!open}>
        {items.length ? renderTop(items) : (
          <>
            <a href="#why" onClick={onLinkClick('#why')}>Why</a>
            <a href="#how-it-works" onClick={onLinkClick('#how-it-works')}>How it Works</a>
            <a href="#path" onClick={onLinkClick('#path')}>Steps</a>
            <a
              href="https://alwayslittx.com/vision-engine"
              className="cta-button"
              target="_top"
              onClick={onLinkClick('https://alwayslittx.com/vision-engine')}
            >
              Vision&nbsp;Engine
            </a>
          </>
        )}
      </div>
    </nav>
  );
};

export default Nav;
